import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import BottomNavbar from "../components/BottomNavbar";
import { ApiConfig } from "../constants/apiConfig";
import {
  fetchJurnalMingguan,
  downloadJurnalMingguanPDF,
  hapusJurnalMingguan,
  submitJurnalMingguan,
} from "../services/jurnalMingguanService";
import logo from "../assets/EPRAJOGO1.png";

function JurnalMingguan() {
  const navigate = useNavigate();
  const siswaId = localStorage.getItem("user_id");

  const [jurnals, setJurnals] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMinggu, setSelectedMinggu] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const actionInFlight = useRef(false);

  const loadJurnals = useCallback(
    async (pageNumber = 1) => {
      if (!siswaId) return;
      setLoading(true);
      const result = await fetchJurnalMingguan(siswaId, pageNumber);
      if (result.success) {
        setJurnals((prev) =>
          pageNumber === 1 ? result.data.data : [...prev, ...result.data.data]
        );
        setHasMore(result.data.next_page_url !== null);
        setPage(pageNumber);
      }
      setLoading(false);
    },
    [siswaId]
  );

  useEffect(() => {
    loadJurnals(1);
  }, [loadJurnals]);

  const handleDownload = async () => {
    if (!selectedMinggu) {
      alert("Pilih minggu terlebih dahulu.");
      return;
    }

    setDownloading(true);
    const result = await downloadJurnalMingguanPDF(siswaId, selectedMinggu);
    if (!result.success) {
      alert(result.message);
    } else {
      setModalOpen(false);
    }
    setDownloading(false);
  };

  const handleJournalAction = async (journal, sending = false) => {
    if (actionInFlight.current) return;
    if (!window.confirm(sending
      ? 'Kirim jurnal ini ke guru untuk dikonfirmasi? Setelah dikirim, jurnal tidak bisa diedit sampai guru meminta revisi.'
      : 'Hapus jurnal ini beserta dokumentasinya? Data yang dihapus tidak dapat dikembalikan.')) return;
    actionInFlight.current = true;
    setBusyId(journal.id);
    try {
      const result = await (sending ? submitJurnalMingguan(journal.id) : hapusJurnalMingguan(journal.id));
      if (!result.success) return alert(result.message || 'Permintaan gagal.');
      setJurnals((current) => sending
        ? current.map((item) => item.id === journal.id ? { ...item, ...result.data, status_verifikasi: 'menunggu' } : item)
        : current.filter((item) => item.id !== journal.id));
    } catch (error) {
      alert(error.message || 'Permintaan gagal.');
    } finally {
      actionInFlight.current = false;
      setBusyId(null);
    }
  };

  return (
    <main className="app-mobile-page">
      <header className="app-brand-header">
        <div className="app-brand-row">
          <img src={logo} alt="Logo E-Prajogo" className="app-brand-logo" />
          <div><p className="app-brand-name">E-PRAJOGO</p><p className="app-brand-school">SMK NEGERI GUDO</p></div>
          <span className="ms-auto rounded-pill px-3 py-2 small fw-semibold" style={{ background: "#ffffff20", color: "#fff" }}>SISWA</span>
        </div>
        <h1 className="app-header-title">Jurnal Mingguan</h1>
        <p className="app-header-subtitle">Rencana kegiatan dan dokumentasi PKL setiap minggu.</p>
      </header>

      <section className="app-content">
        <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
          <div><h2 className="h5 fw-bold mb-1">Rangkuman kegiatan</h2><p className="small text-muted mb-0">Pantau verifikasi dan dokumentasi mingguan.</p></div>
          <button className="btn btn-primary rounded-circle journal-add-button" onClick={() => navigate("/input-jurnal-mingguan")} title="Tambah Jurnal Mingguan" aria-label="Tambah jurnal mingguan">+</button>
        </div>

      {/* Jurnal List */}
      {jurnals.length === 0 && !loading ? (
        <div className="journal-empty-state"><i className="bi bi-journal-text" aria-hidden="true" /><strong>Belum ada jurnal mingguan</strong><span>Rencana kegiatan dan foto pelaksanaanmu akan muncul di sini.</span></div>
      ) : (
        jurnals.map((item) => (
          <article key={item.id} className="journal-entry-card mb-3">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
              <p className="fw-bold mb-0">{item.minggu_ke ? `Minggu ke-${item.minggu_ke}` : 'Minggu belum dipilih'}</p>
              <span className={`badge rounded-pill px-3 py-2 ${item.status_verifikasi === 'draft' ? 'bg-secondary-subtle text-secondary' : item.status_verifikasi === "disetujui" ? "bg-success-subtle text-success" : item.status_verifikasi === "perlu_revisi" ? "bg-warning-subtle text-warning-emphasis" : "bg-primary-subtle text-primary"}`}>
                {item.status_verifikasi === 'draft' ? 'Draft' : item.status_verifikasi === "disetujui" ? "Disetujui guru" : item.status_verifikasi === "perlu_revisi" ? "Perlu revisi" : "Menunggu verifikasi"}
              </span>
            </div>
            {item.status_verifikasi === "perlu_revisi" && item.catatan_revisi && (
              <div className="alert alert-warning py-2 mt-2">
                <strong>Catatan revisi:</strong> {item.catatan_revisi}
              </div>
            )}
            <div className="journal-field"><strong className="journal-field-label">Nama pekerjaan</strong><span>{item.nama_pekerjaan}</span></div>
            <div className="journal-field"><strong className="journal-field-label">Perencanaan kegiatan</strong><span>{item.perencanaan_kegiatan}</span></div>
            <strong className="journal-field-label d-block mb-2">Dokumentasi pelaksanaan</strong>
            {item.foto?.map((foto, idx) => {
              const url = foto.url || (foto.file_path?.startsWith("/")
                ? `${ApiConfig.baseImageUrl}${foto.file_path}`
                : `${ApiConfig.baseImageUrl}/${foto.file_path}`);
              return (
                <div key={idx} className="d-flex align-items-center gap-3 mb-2 journal-photo-row">
                  <img
                    src={url}
                    alt="Foto kegiatan"
                    loading="lazy"
                    className="journal-photo-thumb"
                  />
                  <p className="mb-0 small text-muted">{foto.tanggal} {foto.deskripsi}</p>
                </div>
              );
            })}
            {['draft', 'perlu_revisi'].includes(item.status_verifikasi) && <>
              {item.status_verifikasi === 'draft' && <p className="small text-muted mt-3 mb-0">Belum dikirim ke guru. Lengkapi isian dan foto sebelum mengirim.</p>}
              <div className="d-flex flex-wrap gap-2 mt-3">
                <button
                  className="btn btn-primary btn-sm flex-fill"
                  disabled={busyId !== null}
                  onClick={() => navigate("/input-jurnal-mingguan", { state: { journal: item } })}
                >
                  {item.status_verifikasi === 'draft' ? 'Edit draft' : 'Perbaiki jurnal'}
                </button>
                {item.status_verifikasi === 'draft' && <button className="btn btn-success btn-sm flex-fill" disabled={busyId !== null} onClick={() => handleJournalAction(item, true)}>Kirim ke guru</button>}
                <button
                  className="btn btn-outline-danger btn-sm"
                  disabled={busyId !== null}
                  onClick={() => handleJournalAction(item)}
                >
                  Hapus
                </button>
              </div>
              {busyId === item.id && <p role="status" className="small mt-2 mb-0">Memproses jurnal...</p>}
            </>}
          </article>
        ))
      )}

      {/* Loading Spinner */}
      {loading && (
        <div className="text-center my-3">
          <div className="spinner-border text-primary" role="status" />
        </div>
      )}

      {/* Load More */}
      {!loading && hasMore && (
        <div className="text-center mt-3">
          <button
            className="btn btn-outline-primary"
            onClick={() => loadJurnals(page + 1)}
          >
            Tampilkan Lebih Banyak
          </button>
        </div>
      )}

      {/* Download Button */}
      <button
        className="journal-download-button"
        onClick={() => setModalOpen(true)}
      >
        <i className="bi bi-download me-2" /> Download
      </button>

      {/* Modal */}
      {modalOpen && (
        <div
          className="modal d-block"
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 9999,
            width: "100%",
            height: "100%",
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content journal-modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Download Jurnal Mingguan</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setModalOpen(false)}
                />
              </div>
              <div className="modal-body">
                <label className="form-label">Pilih Minggu ke-</label>
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  <select
                    className="form-select"
                    value={selectedMinggu}
                    onChange={(e) => setSelectedMinggu(e.target.value)}
                  >
                    <option value="">-- Pilih Minggu --</option>
                    {Array.from({ length: 25 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        Minggu ke-{i + 1}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setModalOpen(false)}
                >
                  Batal
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleDownload}
                  disabled={downloading}
                >
                  {downloading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />{" "}
                      Mengunduh...
                    </>
                  ) : (
                    "Download PDF"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </section>
      <BottomNavbar />
    </main>
  );
}

export default JurnalMingguan;
